/**
 * Symbol lock — a chart whose symbol stays put until somebody unlocks it.
 *
 * `ChartGroup.symbolLocked` (owner, 2026-10-06; `docs/plans/symbol-lock.md`).
 * The case it exists for is *JP Market Breadth*: DIA, SPY, QQQ … on five charts,
 * where a screener click or a stray keystroke used to replace one of them.
 * **Only the symbol** — interval, range, indicators and panes stay editable.
 *
 * ## One guard, in two halves
 *
 * Every path that changes a chart's symbol ends in the reducer's `setSymbol`,
 * and that is where the lock is *enforced*: a locked chart's symbol never moves
 * there, and the `sync.symbol` fan-out skips locked charts. Being in the reducer
 * is what stops a new path forgetting it — the screener, the journal, a
 * Community post and the agent bridge all dispatch the same action.
 *
 * What the reducer cannot do is say *where else* a pick should go, or that it
 * was refused: it has no screen to put a toast on. So the other half is here and
 * pure — `routePick` for "chart this" (a screener row, a list, the journal) and
 * `routeTyped` for the symbol box — and `App` turns the answer into a dispatch or
 * a toast. Both halves read `isSymbolLocked`, so they cannot disagree about what
 * locked means.
 *
 * ## What is not guarded
 *
 * Opening a layout and applying a template set the symbol as before: the lock
 * guards *changes*, not the layout's own definition. `replaceLayout`,
 * `addLayout` and `replaceChartGroup` therefore never consult it.
 */

import type { ChartGroup, Layout, UserWorkspace } from "@qufolio/model";

import { chartCycleOrder } from "./fullscreen-cycle";
import { chartGroupConfig } from "./section-config";

/** Whether a chart's symbol is locked. Absent is unlocked — every chart saved before 0051. */
export function isSymbolLocked(group: ChartGroup | undefined): boolean {
  return group?.symbolLocked === true;
}

/** The chart group with this id, wherever it lives. */
export function findChartGroup(ws: UserWorkspace, chartGroupId: string): ChartGroup | undefined {
  for (const layout of ws.layouts) {
    for (const section of layout.sections) {
      const group = chartGroupConfig(section);
      if (group?.id === chartGroupId) return group;
    }
  }
  return undefined;
}

/**
 * Where a symbol request lands.
 *
 * - `chart`: dispatch `setSymbol` to this chart.
 * - `locked`: nothing moves; toast "SYM is locked · Unlock" under this chart.
 */
export type SymbolRoute =
  | { kind: "chart"; chartGroupId: string }
  | { kind: "locked"; chartGroupId: string; symbol: string };

/**
 * A symbol typed at one chart — its box, or a letter typed at the page while it
 * is the active one. A locked chart **refuses**, and the keystroke is not taken
 * (the plan's mock): it never falls through, because typing names a box.
 */
export function routeTyped(ws: UserWorkspace, chartGroupId: string): SymbolRoute {
  const group = findChartGroup(ws, chartGroupId);
  return group && isSymbolLocked(group)
    ? { kind: "locked", chartGroupId, symbol: group.symbol }
    : { kind: "chart", chartGroupId };
}

/**
 * A symbol picked from a list — a screener row, a track list, the journal, a
 * Community post, the data panel's peers.
 *
 * The **active** chart when it is unlocked; otherwise the next unlocked chart in
 * the layout (decision 2, owner 2026-10-06: fall through), in Z order from the
 * active one and wrapping — the order the full-screen ‹ › already reads the
 * grid in, so "next" means the same thing in both places. Only when every chart
 * is locked is it refused, and the toast goes under the active chart.
 *
 * `layout` absent (the active chart is not on a layout we were told about)
 * degrades to the typed rule on the active chart alone.
 */
export function routePick(
  ws: UserWorkspace,
  layout: Layout | undefined,
  activeChartGroupId: string,
): SymbolRoute {
  const active = findChartGroup(ws, activeChartGroupId);
  if (!active || !isSymbolLocked(active)) return { kind: "chart", chartGroupId: activeChartGroupId };
  const refused: SymbolRoute = { kind: "locked", chartGroupId: activeChartGroupId, symbol: active.symbol };
  if (!layout) return refused;

  // Z order is by *section* id; the dispatch wants the chart group's.
  const groupOf = new Map(
    layout.sections.flatMap((s) => {
      const g = chartGroupConfig(s);
      return g ? [[s.id, g] as const] : [];
    }),
  );
  const order = chartCycleOrder(layout).map((id) => groupOf.get(id)!);
  const at = order.findIndex((g) => g.id === activeChartGroupId);
  for (let step = 1; step < order.length; step++) {
    const next = order[(Math.max(at, 0) + step) % order.length]!;
    if (!isSymbolLocked(next)) return { kind: "chart", chartGroupId: next.id };
  }
  return refused;
}

/**
 * The layout panel row's switch: every chart locked, none, or a mix — which
 * draws half-filled. `empty` is a layout with no chart, which has no switch.
 */
export type LayoutLockState = "all" | "none" | "some" | "empty";

export function layoutLockState(layout: Layout | Partial<Layout> | undefined): LayoutLockState {
  const groups = (layout?.sections ?? [])
    .map((s) => chartGroupConfig(s))
    .filter((g): g is ChartGroup => g !== undefined);
  if (groups.length === 0) return "empty";
  const locked = groups.filter(isSymbolLocked).length;
  return locked === 0 ? "none" : locked === groups.length ? "all" : "some";
}
